import { inject, Injectable, NgZone } from '@angular/core';
import { HttpRequest, HttpResponse } from '@angular/common/http';
import Tracker from '@openreplay/tracker';
import trackerAssist from '@openreplay/tracker-assist';
import { environment } from '@env/environment';
import { UserAuthorizationService } from '../user-authorization.service';
import {
  ENVIRONMENT_NAMES,
  LOCAL_STORAGE_KEYS,
} from '@app/shared/constants/constants';

@Injectable({
  providedIn: 'root',
})
// This service was created using the OpenReplay Angular SDK https://docs.openreplay.com/en/sdk/using-or/angular/
export class OpenReplayService {
  public tracker?: Tracker | null;
  private readonly zone = inject(NgZone);
  private readonly userAuthorizationService = inject(UserAuthorizationService);
  constructor() {
    this.initTracker();
  }

  private initTracker() {
    if (
      environment.environmentName === ENVIRONMENT_NAMES.LOCAL ||
      environment.environmentName === ENVIRONMENT_NAMES.DEVELOPMENT
    )
      return;
    this.zone.runOutsideAngular(() => {
      this.tracker = new Tracker({
        projectKey: environment.openReplay.projectKey,
        // Disable secure mode to allow local testing of the tracker
        // __DISABLE_SECURE_MODE: true,
      });

      // Optional plugins
      this.tracker.use(
        // With this plugin can be called the user
        trackerAssist({
          confirmText: `You have an incoming call from Stirista Support. Do you want to answer?`,
        }),
      );
    });
  }

  checkTrackerIsActive() {
    return this.tracker?.isActive();
  }

  public async start() {
    if (
      environment.environmentName === ENVIRONMENT_NAMES.LOCAL ||
      environment.environmentName === ENVIRONMENT_NAMES.DEVELOPMENT
    )
      return;
    this.zone.runOutsideAngular(() => {
      if (this.tracker) {
        const userData =
          this.userAuthorizationService.userData() ||
          JSON.parse(
            localStorage.getItem(LOCAL_STORAGE_KEYS.USER_DATA) || '{}',
          );

        const tenantId =
          this.userAuthorizationService.getCurrentTenant || userData?.tenantId;

        const orgId =
          this.userAuthorizationService.currentOrg() || userData?.orgId;

        return this.tracker.start().then(() => {
          // Set labels, before adding in code please make sure they are configured in preferences/projects/metadata
          this.tracker?.setMetadata('environment', environment.environmentName);
          if (tenantId) {
            this.tracker?.setMetadata('tenantId', tenantId);
          }
          if (orgId) {
            this.tracker?.setMetadata('orgId', orgId);
          }
        });
      } else {
        return {
          sessionID: null,
          sessionToken: null,
          userUUID: null,
        };
      }
    });
  }

  restartTracking(userId?: string, callback?: () => void): void {
    if (
      environment.environmentName === ENVIRONMENT_NAMES.LOCAL ||
      environment.environmentName === ENVIRONMENT_NAMES.DEVELOPMENT
    )
      return;
    if (this.tracker) {
      this.tracker.stop(); // Stop the current tracking
    }

    if (!userId) return;

    // Reinitialize tracking
    this.start().then(() => {
      this.setUserData({ id: userId });
      callback && callback();
    });
  }

  public setUserData(user: { id: string }): void {
    this.zone.runOutsideAngular(() => {
      if (this.tracker && user.id) {
        this.tracker.setUserID(String(user.id));
      }
    });
  }

  public sendEventToReplaySession(
    event: string,
    params: { request: HttpRequest<any>; response: HttpResponse<any> },
  ): void {
    const { request, response } = params;

    this.tracker?.event(event + '[request]', {
      method: request.method,
      url: request.url,
      params: request.params,
    });
    this.tracker?.event(event + '[response]', {
      body: response.body,
      status: response.status,
      headers: response.headers,
    });
  }
}
